import { useQuery } from "@tanstack/react-query";
import { EntityTypeFeaturedIcon } from "@/features/entity-types/components/entity-type-featured-icon";
import { EntityTypeHeaderMeta } from "@/features/entity-types/components/entity-type-header-meta";
import {
	createFileRoute,
	Outlet,
	useNavigate,
	useParams,
	useRouterState,
	useSearch,
} from "@tanstack/react-router";
import {
	Activity,
	Building01,
	ChevronLeft,
	ChevronRight,
	Clipboard,
	Cube01,
	Dataflow01,
	InfoCircle,
	Sliders02,
} from "@untitledui/icons";
import { useContext, useEffect, useState } from "react";
import z from "zod";
import { Tabs } from "@/components/application/tabs/tabs";
import { Button } from "@/components/base/buttons/button";
import { NativeSelect } from "@/components/base/select/select-native";
import { DeleteAssetTypeModal } from "@/components/Organisms/DeleteAssetTypeModal/DeleteAssetTypeModal";
import { DuplicatingEntityTypeModal } from "@/components/Organisms/DuplicatingEntityTypeModal/DuplicatingEntityTypeModal";
import { EntityTypeOptionsMenu } from "@/features/entity-types/components/entity-type-options-menu";
import { useDuplicateEntityType } from "@/features/entity-types/hooks/use-duplicate-entity-type";
import { EntityTypeFormTab } from "@/features/form-editor/components/entity-type-form-tab";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useTypeAccessEditAllowed } from "@/hooks/use-type-access-edit-allowed";
import {
	useDisableAssetType,
	useDuplicateAssetType,
	useEnableAssetType,
	useUpdateAssetType,
} from "@/services/asset/asset.mutations";
import { assetQueryOptions } from "@/services/asset/asset.options";
import type { AssetTypeDisplay } from "@/services/asset/asset.types";
import { authQueryOptions } from "@/services/auth/auth.options";
import { AssetTypeAccessSection } from "@/utils/asset-type-detail/asset-type-access-section";
import { AssetTypeActivitySection } from "@/utils/asset-type-detail/asset-type-activity-section";
import {
	canShowAccessTab,
	canShowActivityTab,
	getAssetTypeDisplayName,
	getEffectiveAssetTypeDrawerMode,
} from "@/utils/asset-type-detail/asset-type-detail.utils";
import { AssetTypeDetailsSection } from "@/utils/asset-type-detail/asset-type-details-section";
import { AssetTypeDrawerContext } from "@/utils/asset-type-detail/asset-type-drawer-context";
import { AssetTypeLifecycleSection } from "@/utils/asset-type-detail/asset-type-lifecycle-section";
import { resolveIconComponent } from "@/utils/icon-catalog";
import { showSuccessToast } from "@/utils/toast.utils";
import { AssetTypeRulesSection } from "./fullRules";

const SearchSchema = z.object({
	tab: z
		.enum(["details", "access", "form", "lifecycle", "rules", "activity"])
		.optional()
		.default("details"),
	mode: z.enum(["view", "edit"]).optional().default("view"),
	presentation: z.enum(["drawer", "modal", "full"]).optional(),
});

export type OrgAssetTypeFullPageTab =
	| "details"
	| "access"
	| "form"
	| "lifecycle"
	| "rules"
	| "activity";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
)({
	component: OrgAssetTypeFullView,
	validateSearch: (search) => SearchSchema.parse(search),
});

function OrgAssetTypeFullView() {
	const { t } = useAppTranslation();
	const navigate = useNavigate();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
	});
	const search = useSearch({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
	});
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const detailQuery = useQuery(
		assetQueryOptions.typeDetail(params.assetTypeId, {
			organizationId: params.orgId,
		}),
	);
	const assetType = detailQuery.data ?? null;
	const drawerCtx = useContext(AssetTypeDrawerContext);
	const updateMutation = useUpdateAssetType();
	const enableMutation = useEnableAssetType();
	const disableMutation = useDisableAssetType();
	const isSavingAny =
		updateMutation.isPending ||
		enableMutation.isPending ||
		disableMutation.isPending;
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);
	const canDeleteQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "DeleteAssetType",
			entityType: "AssetType",
			entityId: params.assetTypeId,
		}),
	});
	const duplicateMutation = useDuplicateAssetType();
	const { duplicate: duplicateAssetType, isPending: isDuplicating } =
		useDuplicateEntityType({
			mutation: duplicateMutation,
			toVariables: (assetTypeId) => ({
				assetTypeId,
				organizationId: params.orgId,
			}),
			onDuplicated: (assetTypeId) => {
				void navigate({
					to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
					params: { orgId: params.orgId, assetTypeId },
					search: { tab: "details", mode: "view", presentation: "full" },
				});
			},
			errorMessage: t("admin.assetTypes.modal.createError"),
		});
	const canEditQuery = useQuery({
		...authQueryOptions.checkAction({
			name: "EditAssetType",
			entityType: "AssetType",
			entityId: params.assetTypeId,
		}),
	});

	const canEditResolved =
		drawerCtx?.canEdit ??
		(assetType ? !!canEditQuery.data : undefined);
	const accessEdit = useTypeAccessEditAllowed(
		"AssetType",
		params.assetTypeId,
	);
	const accessTabAllowed = search.tab === "access" && accessEdit.allowed;
	const drawerMode = getEffectiveAssetTypeDrawerMode(
		(canEditResolved ?? false) || accessTabAllowed,
		search.mode ?? "view",
	);
	const isEditMode = drawerMode === "edit";
	const formSegment = `/configuration/assetTypes/${params.assetTypeId}/full/form`;
	const onFormRoutes = pathname.includes(formSegment);
	const rulesSegment = `/configuration/assetTypes/${params.assetTypeId}/full/rules/`;
	const onRuleDetailRoute = pathname.includes(rulesSegment);
	const activeTab: OrgAssetTypeFullPageTab = onFormRoutes ? "form" : search.tab;

	useEffect(() => {
		if (!assetType || detailQuery.isLoading) return;
		if (search.mode !== "edit") return;
		if (search.tab === "access") {
			if (accessEdit.isPending || accessEdit.allowed) return;
		} else if (canEditResolved !== false) {
			return;
		}
		void navigate({
			to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
			params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
			search: {
				tab: search.tab,
				mode: "view",
				presentation: search.presentation,
			},
		});
	}, [
		assetType,
		accessEdit.allowed,
		accessEdit.isPending,
		canEditResolved,
		detailQuery.isLoading,
		navigate,
		params.assetTypeId,
		params.orgId,
		search.mode,
		search.presentation,
		search.tab,
	]);

	if (onRuleDetailRoute || onFormRoutes) {
		return <Outlet />;
	}

	const handleTabChange = (tab: OrgAssetTypeFullPageTab) => {
		if (tab === "form") {
			void navigate({
				to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
				params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
				search: {
					tab: "form",
					mode: drawerMode,
					presentation: search.presentation,
				},
			});
			return;
		}
		void navigate({
			to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
			params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
			search: { tab, mode: "view", presentation: search.presentation },
		});
	};

	if (!assetType && !detailQuery.isLoading && !detailQuery.isError) {
		return null;
	}

	if (!assetType) {
		return (
			<div className="flex min-h-screen items-center justify-center">
				<div className="text-sm text-gray-500">
					{detailQuery.isLoading
						? t("common.actions.loadingEllipsis")
						: t("admin.assetTypes.drawer.errors.notFound")}
				</div>
			</div>
		);
	}

	const showActivity = canShowActivityTab("organization", assetType);
	const showAccess = canShowAccessTab("organization");
	const tabs = [
		{
			id: "details" as const,
			label: t("admin.assetTypes.drawer.tabs.details"),
			icon: <InfoCircle className="size-4" />,
		},
		...(showAccess
			? [
					{
						id: "access" as const,
						label: t("admin.assetTypes.drawer.tabs.access"),
						icon: <Building01 className="size-4" />,
					},
				]
			: []),
		{
			id: "form" as const,
			label: t("admin.assetTypes.drawer.tabs.form"),
			icon: <Clipboard className="size-4" />,
		},
		{
			id: "lifecycle" as const,
			label: t("admin.assetTypes.drawer.tabs.lifecycle"),
			icon: <Dataflow01 className="size-4" />,
		},
		{
			id: "rules" as const,
			label: t("admin.assetTypes.drawer.tabs.rules"),
			icon: <Sliders02 className="size-4" />,
		},
		...(showActivity
			? [
					{
						id: "activity" as const,
						label: t("admin.assetTypes.drawer.tabs.activity"),
						icon: <Activity className="size-4" />,
					},
				]
			: []),
	];

	const currentTabInfo = tabs.find((tab) => tab.id === activeTab);
	const editModeTabs = currentTabInfo
		? [{ ...currentTabInfo, label: `Edit ${currentTabInfo.label}` }]
		: [];
	const canDelete = !!canDeleteQuery.data && !isEditMode;
	const canDuplicate = !isEditMode;
	const Icon = resolveIconComponent(assetType.icon || assetType.name) ?? Cube01;
	const assetTypeName =
		getAssetTypeDisplayName(assetType) ||
		t("admin.assetTypes.drawer.header.titlePlaceholder");

	const fullPageContext = drawerCtx
		? {
				...drawerCtx,
				activeTab: search.tab,
				mode: drawerMode,
				setMode: (mode: "view" | "edit") => {
					const effectiveMode = getEffectiveAssetTypeDrawerMode(
						search.tab === "access"
							? accessEdit.allowed
							: drawerCtx.canEdit,
						mode,
					);
					void navigate({
						to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
						params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
						search: {
							tab: search.tab,
							mode: effectiveMode,
							presentation: search.presentation,
						},
					});
				},
				isSaving: isSavingAny,
				saveDraft: async () => {
					if (!drawerCtx.isValid || isSavingAny) return;
					if (
						search.tab === "access"
							? !accessEdit.allowed
							: !drawerCtx.canEdit
					)
						return;
					try {
						if (search.tab === "access") {
							if (drawerCtx.draftSubOrgIds === null) {
								await enableMutation.mutateAsync({
									assetTypeId: params.assetTypeId,
									organizationId: params.orgId,
								});
							} else if (drawerCtx.draftSubOrgIds.length === 0) {
								await disableMutation.mutateAsync({
									assetTypeId: params.assetTypeId,
									organizationId: params.orgId,
								});
							} else {
								await updateMutation.mutateAsync({
									assetTypeId: params.assetTypeId,
									data: {
										name: drawerCtx.draftName.trim(),
										description: drawerCtx.draftDescription || null,
										icon: drawerCtx.draftIcon || null,
										typeId: params.assetTypeId,
										statusLifecycleId: assetType.statusLifecycle?.id ?? null,
										geometryType: assetType.geometryType ?? null,
										subOrganizationIds: drawerCtx.draftSubOrgIds,
										locationRequired: drawerCtx.locationRequired,
									},
									organizationId: params.orgId,
								});
							}
							showSuccessToast({
								message: t("admin.assetTypes.drawer.access.toast.saveSuccess"),
							});
						} else {
							const currentSubOrgIds = (
								assetType.assetTypeSubOrganizations ?? []
							)
								.map((s) => s.id)
								.filter((id): id is string => id !== null);
							await updateMutation.mutateAsync({
								assetTypeId: params.assetTypeId,
								data: {
									name: drawerCtx.draftName.trim(),
									description: drawerCtx.draftDescription || null,
									icon: drawerCtx.draftIcon || null,
									typeId: params.assetTypeId,
									statusLifecycleId: assetType.statusLifecycle?.id ?? null,
									geometryType: assetType.geometryType ?? null,
									subOrganizationIds:
										currentSubOrgIds.length > 0 ? currentSubOrgIds : null,
									locationRequired: drawerCtx.locationRequired,
								},
								organizationId: params.orgId,
							});
							showSuccessToast({
								message: t("admin.assetTypes.drawer.details.toast.saveSuccess"),
							});
						}
						void navigate({
							to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
							params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
							search: {
								tab: search.tab,
								mode: "view",
								presentation: search.presentation,
							},
						});
					} catch {
						// Global mutation error handler in root-provider.tsx already shows the error toast
					}
				},
				cancelDraft: () => {
					drawerCtx.setDraftName(
						getAssetTypeDisplayName(assetType) || assetType.name || "",
					);
					drawerCtx.setDraftDescription(assetType.description ?? "");
					drawerCtx.setDraftIcon(assetType.icon ?? "");
					const subOrgIds = (assetType.assetTypeSubOrganizations ?? [])
						.map((s) => s.id)
						.filter((id): id is string => id !== null);
					drawerCtx.setDraftSubOrgIds(
						subOrgIds.length > 0
							? subOrgIds
							: assetType.enabled === false
								? []
								: null,
					);
					void navigate({
						to: "/org-admin/$orgId/configuration/assetTypes/$assetTypeId/full",
						params: { orgId: params.orgId, assetTypeId: params.assetTypeId },
						search: {
							tab: search.tab,
							mode: "view",
							presentation: search.presentation,
						},
					});
				},
			}
		: null;

	return (
		<AssetTypeDrawerContext.Provider value={fullPageContext}>
			<div className="flex min-h-screen flex-col bg-white">
				<div className="border-b border-gray-200 px-6 pb-0 pt-4">
					<nav
						aria-label={t("admin.shared.formEditor.a11y.breadcrumb")}
						className="mb-3 flex items-center gap-1"
					>
						<Button
							color="tertiary"
							size="sm"
							onClick={() =>
								void navigate({
									to: "/org-admin/$orgId/configuration/assetTypes",
									params: { orgId: params.orgId },
								})
							}
						>
							{t("admin.assetTypes.table.title")}
						</Button>
						<ChevronRight className="size-3.5 text-gray-400" />
						<span className="text-sm font-medium text-gray-900">
							{assetTypeName}
						</span>
					</nav>
					<div className="flex items-center justify-between pb-4">
						<div className="flex items-center gap-3">
							<EntityTypeFeaturedIcon icon={Icon} size="lg" />
							<div className="flex min-w-0 flex-col gap-1">
								<h1 className="text-[24px] font-semibold text-gray-900">
									{assetTypeName}
								</h1>
								<EntityTypeHeaderMeta
									createdAt={assetType.createdAt}
									updatedAt={assetType.updatedAt}
								/>
							</div>
						</div>
						<div className="flex items-center gap-3">
							<EntityTypeOptionsMenu
								duplicateLabel={
									canDuplicate
										? t("admin.assetTypes.table.actions.duplicate")
										: undefined
								}
								onDuplicate={
									canDuplicate
										? () => duplicateAssetType(params.assetTypeId)
										: undefined
								}
								deleteLabel={
									canDelete
										? t("admin.assetTypes.drawer.footer.deleteAssetType")
										: undefined
								}
								onDelete={
									canDelete ? () => setDeleteModalOpen(true) : undefined
								}
							/>
							<Button
								color="secondary"
								iconLeading={ChevronLeft}
								onClick={() =>
									void navigate({
										to: "/org-admin/$orgId/configuration/assetTypes",
										params: { orgId: params.orgId },
									})
								}
							>
								{t("admin.assetTypes.drawer.header.backToList")}
							</Button>
						</div>
					</div>
					<div className="space-y-4">
						<NativeSelect
							aria-label={t("admin.assetTypes.drawer.header.subtitle")}
							value={activeTab}
							onChange={(event) =>
								handleTabChange(event.target.value as OrgAssetTypeFullPageTab)
							}
							options={(isEditMode ? editModeTabs : tabs).map((tab) => ({
								label: tab.label,
								value: tab.id,
							}))}
							className="w-80 md:hidden"
						/>
						<Tabs
							selectedKey={activeTab}
							onSelectionChange={(key) =>
								handleTabChange(key as OrgAssetTypeFullPageTab)
							}
							className="w-2/5 max-md:hidden"
						>
							<Tabs.List
								type="underline"
								items={isEditMode ? editModeTabs : tabs}
								className="w-full"
								fullWidth
							>
								{(tab) => <Tabs.Item id={tab.id} label={tab.label} />}
							</Tabs.List>
						</Tabs>
					</div>
				</div>
				<div className="flex-1 overflow-y-auto p-8">
					<OrgAssetTypeFullIndexTabContent
						tab={activeTab}
						assetType={assetType}
						orgId={params.orgId}
					/>
				</div>
			</div>
			<DeleteAssetTypeModal
				isOpen={deleteModalOpen}
				setIsOpen={setDeleteModalOpen}
				handleClose={() => setDeleteModalOpen(false)}
				onDeleteSuccess={() =>
					void navigate({
						to: "/org-admin/$orgId/configuration/assetTypes",
						params: { orgId: params.orgId },
					})
				}
				assetTypeId={assetType.id}
				assetTypeName={assetTypeName}
				organizationId={params.orgId}
				scope="organization"
			/>
			<DuplicatingEntityTypeModal
				isOpen={isDuplicating}
				label={t("admin.assetTypes.duplicate.inProgress")}
				testId="assettype-full-duplicate-in-progress"
			/>
		</AssetTypeDrawerContext.Provider>
	);
}

interface OrgAssetTypeFullIndexTabContentProps {
	tab: OrgAssetTypeFullPageTab;
	assetType: AssetTypeDisplay;
	orgId: string;
}

export function OrgAssetTypeFullIndexTabContent({
	tab,
	assetType,
	orgId,
}: OrgAssetTypeFullIndexTabContentProps) {
	const { t } = useAppTranslation();
	void t;
	const drawerCtx = useContext(AssetTypeDrawerContext);
	const showActivity = canShowActivityTab("organization", assetType);

	if (!drawerCtx) return null;

	switch (tab) {
		case "details":
			return <AssetTypeDetailsSection />;
		case "access":
			return <AssetTypeAccessSection orgId={orgId} />;
		case "form":
			return (
				<EntityTypeFormTab
					entity="asset"
					typeId={assetType.id}
					scope="org"
					orgId={orgId}
					mutationOrganizationId={orgId}
				/>
			);
		case "lifecycle":
			return (
				<AssetTypeLifecycleSection organizationId={orgId} variant="full" />
			);
		case "rules":
			return <AssetTypeRulesSection />;
		case "activity":
			return showActivity ? (
				<AssetTypeActivitySection
					assetType={assetType}
					variant="full"
					organizationId={orgId}
				/>
			) : null;
		default:
			return <AssetTypeDetailsSection />;
	}
}
